import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../api';

/**
 * BD-06 — the full pre-meeting brief, shown at stage 1 of the meeting page.
 * The Slack message is the short version of the same thing. Every factual line
 * shows its source; questions and ways to help are labelled as inference.
 */

interface Source { label: string; url: string | null }
interface Line { text: string; source: Source; date?: string }
interface Attendee {
  name: string;
  email: string;
  status: 'known' | 'first_meeting' | 'ambiguous';
  title?: string | null;
  org?: string | null;
  note?: Line;
  lastMet?: Line | null;
  ownerName?: Line | null;
  history?: Line[];
  commitments?: Line[];
  claims?: Line[];
}
interface Sections {
  attendees: Attendee[];
  deal: Line | null;
  news: Line[];
  proof: Line[];
  questions: { inference: boolean; items: string[] };
  help: { inference: boolean; items: string[] };
  coaching: Line | null;
}
interface Brief {
  id: number;
  status: string;
  skip_reason: string | null;
  starts_at: string;
  sent_at: string | null;
  sections: Sections | null;
}

const SourceTag: React.FC<{ source: Source }> = ({ source }) => (
  source.url
    ? <a href={source.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline ml-1">({source.label})</a>
    : <span className="text-xs text-gray-400 ml-1">({source.label})</span>
);

const Lines: React.FC<{ lines?: Line[] }> = ({ lines }) => (
  <ul className="space-y-1">
    {(lines || []).map((l, i) => (
      <li key={i} className="text-sm text-gray-700">{l.text}<SourceTag source={l.source} /></li>
    ))}
  </ul>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-5 mb-2">{children}</h4>
);

// A pending brief carries a reason when something the person can change is holding
// it back; it is sent as soon as that is fixed, if the meeting has not started.
const WAITING_REASON: Record<string, string> = {
  step_off: 'Prep briefs is switched off for you',
  no_mapping: 'your Slack account is not linked',
  not_permitted: 'Slack notifications are off for you',
};

const reasonNote = (b: Brief): string => {
  if (!b.skip_reason) return '';
  if (b.status === 'skipped') return ` (${b.skip_reason.replace(/_/g, ' ')})`;
  if (b.status === 'pending') {
    return WAITING_REASON[b.skip_reason]
      ? ` Waiting: ${WAITING_REASON[b.skip_reason]}.`
      : ' Slack delivery will be retried.';
  }
  return '';
};

const STATUS_NOTE: Record<string, string> = {
  pending: 'Goes to you in Slack before the meeting.',
  sending: 'Being sent to you in Slack now.',
  sent: 'Sent to you in Slack.',
  skipped: 'Not sent to Slack',
  failed: 'Slack delivery failed',
};

const MeetingBriefPanel: React.FC<{ meetingId: number }> = ({ meetingId }) => {
  const [brief, setBrief] = useState<Brief | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    api.get(`/api/bd/briefs/meeting/${meetingId}`)
      .then((res) => { if (active) setBrief(res.data?.brief || null); })
      .catch((err) => {
        if (!active) return;
        setBrief(null);
        if (err?.response?.status !== 404) setFailed(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [meetingId]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-400 mb-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading brief…</div>;
  }
  if (failed) return <p className="text-sm text-red-600 mb-6">The brief could not be loaded.</p>;
  if (!brief || !brief.sections) {
    return <p className="text-sm text-gray-400 mb-6">No brief for this meeting yet. It is prepared once the meeting is on your calendar.</p>;
  }

  const s = brief.sections;
  const note = STATUS_NOTE[brief.status] || '';
  return (
    <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
      <p className="text-xs text-gray-500 mb-1">
        {note}{reasonNote(brief)}
      </p>

      {s.attendees.map((a) => (
        <div key={a.email}>
          <Heading>{a.name}{a.title ? ` — ${a.title}` : ''}{a.org ? `, ${a.org}` : ''}</Heading>
          {a.status !== 'known' && a.note ? (
            <Lines lines={[a.note]} />
          ) : (
            <>
              <Lines lines={[a.lastMet, a.ownerName].filter(Boolean) as Line[]} />
              {(a.history || []).length > 0 && <><p className="text-xs text-gray-500 mt-2 mb-1">Recent interactions</p><Lines lines={a.history} /></>}
              {(a.commitments || []).length > 0 && <><p className="text-xs text-gray-500 mt-2 mb-1">Open commitments</p><Lines lines={a.commitments} /></>}
              {(a.claims || []).length > 0 && <><p className="text-xs text-gray-500 mt-2 mb-1">What we know</p><Lines lines={a.claims} /></>}
            </>
          )}
        </div>
      ))}

      {s.deal && <><Heading>Deal status</Heading><Lines lines={[s.deal]} /></>}
      {s.news.length > 0 && (
        <>
          <Heading>What&apos;s new (last 30 days)</Heading>
          <ul className="space-y-1">
            {s.news.map((n, i) => (
              <li key={i} className="text-sm text-gray-700">{n.text}{n.date ? ` — ${n.date}` : ''}<SourceTag source={n.source} /></li>
            ))}
          </ul>
        </>
      )}
      {s.proof.length > 0 && <><Heading>Relevant TSG work</Heading><Lines lines={s.proof} /></>}
      {s.coaching && <><Heading>Coaching focus</Heading><Lines lines={[s.coaching]} /></>}

      {s.questions.items.length > 0 && (
        <>
          <Heading>Questions worth asking <span className="normal-case font-normal text-amber-600">· inference</span></Heading>
          <ol className="list-decimal ml-5 space-y-1">{s.questions.items.map((q, i) => <li key={i} className="text-sm text-gray-700">{q}</li>)}</ol>
        </>
      )}
      {s.help.items.length > 0 && (
        <>
          <Heading>Ways TSG could help <span className="normal-case font-normal text-amber-600">· inference</span></Heading>
          <ul className="list-disc ml-5 space-y-1">{s.help.items.map((h, i) => <li key={i} className="text-sm text-gray-700">{h}</li>)}</ul>
        </>
      )}
    </div>
  );
};

export default MeetingBriefPanel;
